<template>
  <div class="image-viewer" v-if="photo">
    <div class="image-viewer__wrapper">
      <img
        :key="photo.src"
        :src="photo.src"
        :alt="photo.fileName"
        class="image-viewer__image image-viewer__image--visible"
        ref="imageRef"
      />

      <!-- Person squares overlay -->
      <div class="person-squares-overlay" v-if="showFaceBoxes && photo.faces">
        <div
          v-for="face in photo.faces"
          :key="face.id"
          class="person-square"
          :style="getExpandedFaceStyle(face)"
          :class="getFaceClasses(face)"
          @mouseover="$emit('face-highlight', face.id)"
          @mouseleave="$emit('face-clear-highlight')"
          @click="handleFaceClick($event, face.id)"
          @contextmenu.prevent="$emit('face-context-menu', {event: $event, faceId: face.id})"
        ></div>

        <!-- Highlight tooltip -->
        <div
          v-if="highlightTooltipText && highlightedFaceId !== null"
          class="highlight-tooltip"
          :class="getTooltipClasses()"
          :style="highlightTooltipStyle"
          v-html="highlightTooltipText"
        ></div>
      </div>
    </div>
  </div>
  <div v-else class="image-viewer image-viewer--loading">
    <CIcon name="cipCircleLoader" size="custom-size" :width="48" class="rotating"/>
  </div>
</template>

<script>
import TagsCompletionStatus from "@/domain/core/constant/tagsCompletionStatus"

export default {
  name: "ImageViewer",
  props: {
    photo: {
      type: Object,
      default: null
    },
    selectedFaceId: {
      type: [Number, String],
      default: null
    },
    highlightedFaceId: {
      type: [Number, String],
      default: null
    },
    showFaceBoxes: {
      type: Boolean,
      default: true
    },
    // clusters prop removed - cluster data now in face.tags directly
    clusterHighlightPattern: {
      type: String,
      default: ''
    },
    navigationDirection: {
      type: Number,
      default: 0
    }
  },
  computed: {
    highlightedFace() {
      if (!this.photo?.faces || this.highlightedFaceId === null) return null
      return this.photo.faces.find(f => f.id === this.highlightedFaceId)
    },
    highlightedCluster() {
      // Cluster data is now in face.tags directly (API change)
      if (!this.highlightedFace?.cluster_id) return null
      return {
        id: this.highlightedFace.cluster_id,
        tags: this.highlightedFace.tags || {},
        picacode: this.highlightedFace.picacode
      }
    },
    highlightTooltipText() {
      if (!this.clusterHighlightPattern || !this.highlightedFace) return ''
      // Cluster tags are now flat in face.tags (API change)
      const tags = this.highlightedFace.tags || {}
      let hasReplacement = false
      const result = this.clusterHighlightPattern.replace(/\{(\w+)\}/g, (match, key) => {
        const value = tags[key]
        if (value !== undefined && value !== null && value !== '') {
          hasReplacement = true
          return value
        }
        return ''
      })
      // Don't show tooltip if no actual values were replaced
      if (!hasReplacement) return ''
      return result
    },
    highlightTooltipStyle() {
      if (!this.highlightedFace) return {}
      const face = this.highlightedFace
      // Position tooltip below the face box
      return {
        left: (face.face_box.x + face.face_box.width / 2) + '%',
        top: (face.face_box.y + face.face_box.height) + '%'
      }
    }
  },
  methods: {
    handleFaceClick(event, faceId) {
      // Ignore right clicks (button === 2)
      if (event.button === 2) return
      this.$emit('face-select', faceId)
    },
    getExpandedFaceStyle(face) {
      // Coordinates are already expanded server-side in face_box
      return {
        left: face.face_box.x + '%',
        top: face.face_box.y + '%',
        width: face.face_box.width + '%',
        height: face.face_box.height + '%'
      }
    },
    getFaceClasses(face) {
      const isSelected = this.selectedFaceId === face.id
      const isHighlighted = this.highlightedFaceId === face.id
      const isComplete = face.tagsCompletionStatus === TagsCompletionStatus.COMPLETE

      return {
        'selected': isSelected,
        'highlighted': isHighlighted && !isSelected,
        'complete': isComplete && !isSelected,
        'incomplete': !isComplete && !isSelected
      }
    },
    getTooltipClasses() {
      if (!this.highlightedFace) return {}
      const isSelected = this.selectedFaceId === this.highlightedFaceId
      return {
        'tooltip--selected': isSelected,
        'tooltip--default': !isSelected
      }
    }
  }
}
</script>

<style scoped>

</style>